import type { RouteRecordRaw } from 'vue-router';
import type { HydrationContext, SystemEntityStore, WorkspaceEntityStore, WorkspaceHydration, WorkspaceRuntime } from '#layers/runtime/public';
import { createError } from '#app/composables/error';
import { currentUserRolesHydration, entitlementResourcesHydration } from '#layers/access/public';
import { workspaceBlocksHydration } from '#layers/block-engine/public';
import { workspaceSchemaHydration } from '#layers/data-model/public';
import { workspaceDataConnectorsHydration, workspaceExtensionsHydration } from '#layers/extensions/public';
import { workspacePagesHydration, workspaceSpacesHydration } from '#layers/spaces/public';
import { prepareWorkspaceSpaceRoutes } from '#layers/ui-workspace/public';
import { workspaceInfoHydration, workspaceKeyStorageHydration, workspaceSettingsHydration } from '#layers/workspace/public';

/** What publication needs beyond the runtime itself: the router has no cache to derive routes from. */
export interface PreparedWorkspaceRoutes {
	routes: RouteRecordRaw[];
}

interface HydrateWorkspaceOptions extends HydrationContext {
	runtime: WorkspaceRuntime<WorkspaceEntityStore>;
	systemStore: SystemEntityStore;
	translate: (key: string) => string;
}

/**
 * Every commit-gating definition a candidate runtime loads before it publishes; stores read them through `use()`.
 * Resolved per call rather than at import so a spec that mocks a layer barrel without these exports still boots the app.
 */
function workspaceHydrations(): readonly WorkspaceHydration[] {
	return [
		entitlementResourcesHydration,
		currentUserRolesHydration,
		workspaceInfoHydration,
		workspaceBlocksHydration,
		workspaceSettingsHydration,
		workspaceKeyStorageHydration,
		workspaceExtensionsHydration,
		workspaceDataConnectorsHydration,
		workspacePagesHydration,
	];
}

/**
 * Loads every commit-gating dependency into the candidate runtime; stores read them once the runtime
 * is active. Never resolve the ambient (still-active) workspace store here.
 */
export async function hydrateWorkspace({
	runtime,
	systemStore,
	translate,
	userId,
}: HydrateWorkspaceOptions): Promise<PreparedWorkspaceRoutes> {
	const context: HydrationContext = { userId };

	// Registers the workspace collections first, so every other query runs against them.
	await workspaceSchemaHydration.hydrate(runtime, context);

	// Spaces hydrate alongside the list rather than in it, so their rows come back for the routes.
	const [spaces] = await Promise.all([
		workspaceSpacesHydration.hydrate(runtime, context),
		requireWorkspaceRecord({ slug: runtime.slug, systemStore, translate }),
		...workspaceHydrations().map((hydration) => hydration.hydrate(runtime, context)),
	]);

	// Every hydration has settled, pages included, so `space.pages` is complete when the routes read it.
	const routes = prepareWorkspaceSpaceRoutes(spaces);

	return { routes };
}

async function requireWorkspaceRecord({
	slug,
	systemStore,
	translate,
}: { slug: string; systemStore: SystemEntityStore; translate: (key: string) => string }) {
	const workspace = await systemStore.MonospaceWorkspace.findFirst({
		fetchPolicy: 'fetch-only',
		filter: { apiName: { _eq: slug } },
	});

	if (workspace) return;

	throw createError({
		statusCode: 404,
		statusMessage: translate('workspace.not_found'),
	});
}
